import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface StorageUsage {
  asset_photos_bytes: number;
  asset_documents_bytes: number;
  asset_attachments_bytes: number;
  condition_images_bytes: number;
  support_attachments_bytes: number;
  location_documents_bytes: number;
  check_response_photos_bytes: number;
  check_response_videos_bytes: number;
  check_response_images_bytes: number;
  incident_attachments_bytes: number;
  incident_documents_bytes: number;
  total_bytes: number;
}

interface StorageUsagePanelProps {
  businessId: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

interface CategoryRowProps {
  label: string;
  bytes: number;
  totalBytes: number;
  color: string;
  icon: React.ReactNode;
}

const CategoryRow: React.FC<CategoryRowProps> = ({ label, bytes, totalBytes, color, icon }) => {
  const pct = totalBytes > 0 ? (bytes / totalBytes) * 100 : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {formatBytes(bytes)}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ minWidth: 40, textAlign: 'right' }}>
            {pct.toFixed(1)}%
          </Typography>
        </Stack>
      </Stack>
      <Tooltip title={`${formatBytes(bytes)} (${pct.toFixed(1)}%)`} placement="top">
        <LinearProgress
          variant="determinate"
          value={Math.min(pct, 100)}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 },
          }}
        />
      </Tooltip>
    </Box>
  );
};

const CATEGORIES = [
  {
    key: 'asset_photos_bytes' as keyof StorageUsage,
    label: 'Asset Photos',
    color: '#0288d1',
    icon: <ImageIcon fontSize="small" />,
  },
  {
    key: 'asset_documents_bytes' as keyof StorageUsage,
    label: 'Asset Documents',
    color: '#1976d2',
    icon: <DescriptionIcon fontSize="small" />,
  },
  {
    key: 'asset_attachments_bytes' as keyof StorageUsage,
    label: 'Asset Attachments',
    color: '#388e3c',
    icon: <AttachFileIcon fontSize="small" />,
  },
  {
    key: 'condition_images_bytes' as keyof StorageUsage,
    label: 'Condition Images',
    color: '#f57c00',
    icon: <ImageIcon fontSize="small" />,
  },
  {
    key: 'check_response_photos_bytes' as keyof StorageUsage,
    label: 'Check Response Photos',
    color: '#7b1fa2',
    icon: <FolderIcon fontSize="small" />,
  },
  {
    key: 'check_response_videos_bytes' as keyof StorageUsage,
    label: 'Check Response Videos',
    color: '#c2185b',
    icon: <VideocamIcon fontSize="small" />,
  },
  {
    key: 'location_documents_bytes' as keyof StorageUsage,
    label: 'Location Documents', // renamed to "Site Documents" when site terminology is enabled
    color: '#00796b',
    icon: <LocationOnIcon fontSize="small" />,
  },
  {
    key: 'support_attachments_bytes' as keyof StorageUsage,
    label: 'Support Ticket Attachments',
    color: '#d32f2f',
    icon: <SupportAgentIcon fontSize="small" />,
  },
  {
    key: 'incident_attachments_bytes' as keyof StorageUsage,
    label: 'Incident Attachments',
    color: '#e65100',
    icon: <ReportProblemIcon fontSize="small" />,
  },
  {
    key: 'incident_documents_bytes' as keyof StorageUsage,
    label: 'Incident Documents',
    color: '#bf360c',
    icon: <DescriptionIcon fontSize="small" />,
  },
];

const StorageUsagePanel: React.FC<StorageUsagePanelProps> = ({ businessId }) => {
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [useSiteTerminology, setUseSiteTerminology] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/businesses/${businessId}`)
      .then((res) => {
        if (!cancelled) {
          setUseSiteTerminology(
            res.data?.use_site_terminology === 1 || res.data?.use_site_terminology === true
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<StorageUsage>(
        `${API_BASE_URL}/businesses/${businessId}/storage-usage`
      );
      setUsage(res.data);
      setLastFetched(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load storage usage.');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Bunny Cloud Storage Usage</Typography>
          <Typography variant="body2" color="text.secondary">
            Tracked file sizes across photos, documents, condition images, check media, incident attachments, and location documents (on Bunny).
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchUsage}
          disabled={loading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !usage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {usage && (
        <>
          {/* Total summary card */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.200',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                Total Storage Used
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                {formatBytes(usage.total_bytes)}
              </Typography>
            </Box>
            {loading && <CircularProgress size={20} />}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Breakdown by category
          </Typography>

          {CATEGORIES.map((cat) => (
            <CategoryRow
              key={cat.key}
              label={
                cat.key === 'location_documents_bytes' && useSiteTerminology
                  ? 'Site Documents'
                  : cat.label
              }
              bytes={usage[cat.key] as number}
              totalBytes={usage.total_bytes}
              color={cat.color}
              icon={cat.icon}
            />
          ))}

          {lastFetched && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
              Last updated: {lastFetched.toLocaleTimeString()}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default StorageUsagePanel;
